import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import RequiredMark from '@/shein-platform/components/RequiredMark';
import SheinSwitch from '@/shein-platform/components/SheinSwitch';
import { nextTemplatePriority } from '@/shein-platform/utils/sheinTemplatePriority';
import { styleNoFromSupplierCode } from '@/shein-platform/utils/sheinStyleNo';
import { sheinPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

/**
 * 从发品页把当前表单另存为产品模板：元信息弹窗（字段/样式对齐属性库保存弹窗）。
 * 款号默认同步自商家货号，可单独修改。
 */
export default function SaveProductTemplateMetaModal({
  open,
  shopId,
  categoryId = '',
  productTypeId = '',
  categoryPathLabel = '',
  getFormSnapshot,
  runWithoutUnloadPrompt,
  onClose,
  onSaved,
}) {
  useTranslation('workbench');
  const [name, setName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [remark, setRemark] = useState('');
  const [autoRuleEnabled, setAutoRuleEnabled] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);
  const [priorityLoading, setPriorityLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setRemark('');
    setAutoRuleEnabled(false);
    setSortOrder(0);
    setSaving(false);
    const formSnapshot = typeof getFormSnapshot === 'function' ? getFormSnapshot() : null;
    setStyleNo(styleNoFromSupplierCode(formSnapshot?.supplierCode || ''));
    // 仅在打开时初始化；不把 getFormSnapshot 放入依赖，避免编辑中被重置
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !shopId || !productTypeId) return undefined;
    let cancelled = false;
    setPriorityLoading(true);
    const loadPriority = async () => {
      try {
        const res = await sheinPlatformAPI.listProductTemplates(shopId, {
          productTypeId,
          page: 1,
          pageSize: 100,
        });
        if (cancelled) return;
        setSortOrder(nextTemplatePriority(res?.items));
      } catch {
        if (!cancelled) setSortOrder(0);
      } finally {
        if (!cancelled) setPriorityLoading(false);
      }
    };
    // 打开弹窗拉优先级时也可能触发工作区跳转；抑制发品页 beforeunload 误报
    if (typeof runWithoutUnloadPrompt === 'function') {
      void runWithoutUnloadPrompt(loadPriority);
    } else {
      void loadPriority();
    }
    return () => { cancelled = true; };
  }, [open, shopId, productTypeId, runWithoutUnloadPrompt]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const submit = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!shopId) {
      showToast(chartText('returnList.pickShop', fixedUi("请先选择店铺")), 'error');
      return;
    }
    const title = String(name || '').trim();
    if (!title) {
      showToast(chartText('saveTpl.needName', fixedUi("请填写模板名称")), 'error');
      return;
    }
    const styleNoValue = String(styleNo || '').trim().slice(0, 128);
    if (!styleNoValue) {
      showToast(chartText('saveTpl.needStyle', fixedUi("请填写款号")), 'error');
      return;
    }
    if (!categoryId || !productTypeId) {
      showToast(chartText('saveTpl.needCategory', fixedUi("缺少适用类目")), 'error');
      return;
    }
    const formSnapshot = typeof getFormSnapshot === 'function' ? getFormSnapshot() : null;
    if (!formSnapshot || typeof formSnapshot !== 'object') {
      showToast(chartText('saveTpl.needContent', '无法读取当前商品内容'), 'error');
      return;
    }

    const persist = async () => {
      setSaving(true);
      try {
        await sheinPlatformAPI.createProductTemplate(shopId, {
          name: title,
          styleNo: styleNoValue,
          remark: String(remark || '').trim() || undefined,
          productTypeId,
          categoryId,
          categoryPathLabel: categoryPathLabel || undefined,
          autoRuleEnabled,
          sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
          payload: {
            form: formSnapshot,
          },
        });
        showToast(chartText('saveTpl.savedProduct', '已保存为产品模板'), 'success');
        onSaved?.();
        onClose?.();
      } catch (e) {
        showToast(e?.message || chartText('saveTpl.saveProductFail', '保存产品模板失败'), 'error');
      } finally {
        setSaving(false);
      }
    };

    if (typeof runWithoutUnloadPrompt === 'function') {
      await runWithoutUnloadPrompt(persist);
    } else {
      await persist();
    }
  };

  return createPortal(
    <div className="spo-batch-modal spo-attr-tpl-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={chartText('moduleShell.close', fixedUi("关闭"))}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="spo-batch-modal__panel spo-attr-tpl-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-product-tpl-save-meta-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-product-tpl-save-meta-title">{chartText('saveTpl.saveProduct', fixedUi("保存产品模板"))}</h2>
          <button type="button" className="spo-batch-modal__close" aria-label={chartText('moduleShell.close', fixedUi("关闭"))} disabled={saving} onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit}>
          <div className="spo-batch-modal__body">
            <div className="spo-wizard__form spo-template-create-form">
              <label className="spo-field spo-field--full">
                <span className="spo-field__label">{chartText('productTemplate.colName', fixedUi("模板名称"))} <RequiredMark /></span>
                <input
                  className="spo-input"
                  value={name}
                  disabled={saving}
                  maxLength={128}
                  placeholder={chartText('saveTpl.namePhProduct', '如：女装夏季基础款')}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="spo-field spo-field--full">
                <span className="spo-field__label">{chartText('productTemplate.colStyleNo', fixedUi("款号"))} <RequiredMark /></span>
                <input
                  className="spo-input"
                  value={styleNo}
                  disabled={saving}
                  maxLength={128}
                  placeholder={chartText('saveTpl.sheinStylePh', '默认从商家货号提取，可单独修改')}
                  onChange={(e) => setStyleNo(e.target.value)}
                />
                <p className="spo-wizard__inline-hint">
                  打开时默认从当前商品商家货号提取，可再手动修改。
                </p>
              </label>
              <label className="spo-field spo-field--full">
                <span className="spo-field__label">{chartText('productTemplate.colRemark', fixedUi("备注"))}</span>
                <input
                  className="spo-input"
                  value={remark}
                  disabled={saving}
                  maxLength={200}
                  placeholder={chartText('publishSpec.patternOptional', fixedUi("可选"))}
                  onChange={(e) => setRemark(e.target.value)}
                />
              </label>
              <div className="spo-field spo-field--full spo-template-auto-rule">
                <div className="spo-template-auto-rule__switch">
                  <SheinSwitch
                    checked={autoRuleEnabled}
                    disabled={saving}
                    ariaLabel={chartText('productTemplate.colAutoRule', '自动规则')}
                    onChange={setAutoRuleEnabled}
                  />
                  <span>{chartText('productTemplate.colAutoRule', fixedUi("自动规则"))}</span>
                </div>
                <p className="spo-wizard__inline-hint">
                  {chartText('saveTpl.autoHint', '开启后，发品选中该类目时自动带出。优先级仅在同类目内比较，数字越小越优先。')}
                </p>
                <label className="spo-field">
                  <span className="spo-field__label">{chartText('productTemplate.colPriority', fixedUi("优先级"))}</span>
                  <input
                    className="spo-input"
                    type="number"
                    step={1}
                    value={sortOrder}
                    disabled={saving || priorityLoading}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setSortOrder(Number.isFinite(next) ? next : 0);
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
          <div className="spo-batch-modal__foot">
            <button type="button" className="spo-btn spo-btn--ghost" disabled={saving} onClick={onClose}>{chartText('shop.cancel', fixedUi("取消"))}</button>
            <button type="submit" className="spo-btn spo-btn--primary" disabled={saving}>
              {saving ? chartText('tiktokPublish.saving', fixedUi("保存中…")) : chartText('shop.save', fixedUi("保存"))}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
